<template>
    <div :class="computedColClass">
        <div :class="wrapperClass">

            <!-- CHECKBOX -->
            <input
                :type="'checkbox'"
                :id="inputId"
                :name="fieldName"
                :checked="modelValue"
                @change="$emit('update:modelValue', $event.target.checked)"
                :disabled="disabled"
                :readonly="readonly"
                :class="computedInputClass"
                :style="validationStyle"
            />

            <!-- LABEL -->
            <label :for="inputId" :class="labelClass">
                <slot name="title">
                    {{ title ? toTitleCase(title) : '' }}
                </slot>
                <sup v-if="req" :class="spanClass">*</sup>
            </label>

            <!-- ERROR MESSAGE -->
            <Error
                v-if="hasError"
                :id="errorId"
                :message="errorMessage"
                :bootstrap="bootstrap"
            />

        </div>
    </div>
</template>

<script setup>
import { computed } from "vue";
import { useValidationStore } from '../../stores/validationStore.js';

const props = defineProps({
    modelValue: Boolean,
    title: String,
    inputId: { type: String, default: null },
    field: { type: String, required: true },
    col: String,
    req: { type: Boolean, default: false },
    disabled: Boolean,
    readonly: Boolean,
    bootstrap: { type: Boolean, default: false }
});

const validationStore = useValidationStore();

// Computed field name
const fieldName = computed(() => {
    const shortKey = props.field.split(".").pop();
    return validationStore.hasError(props.field) ? props.field
        : validationStore.hasError(shortKey) ? shortKey
            : props.field;
});

// Computed IDs
const inputId = computed(() => props.inputId ?? fieldName.value);
const errorId = computed(() => `error-${inputId.value}`);

// Validation state
const errorMessage = computed(() => validationStore.getErrors(fieldName.value));
const hasError = computed(() => validationStore.hasError(fieldName.value));

// Col class
const computedColClass = computed(() => {
    if (props.col) return props.col;
    return props.bootstrap
        ? 'col-12 col-md-6 col-lg-4'
        : 'col-span-12 md:col-span-6 lg:col-span-4';
});

// Wrapper class
const wrapperClass = computed(() => props.bootstrap ? 'form-check mb-3' : 'flex items-center mb-3 space-x-2');

// Input class
const computedInputClass = computed(() => {
    if (props.bootstrap) {
        return [
            'form-check-input',
            hasError.value ? 'is-invalid' : ''
        ].join(' ');
    } else {
        return [
            'rounded border-gray-300 text-green-600 focus:ring-green-500 focus:ring-2',
            hasError.value ? 'border-red-500' : ''
        ].join(' ');
    }
});

// Label class
const labelClass = computed(() => props.bootstrap ? 'form-check-label' : 'text-gray-700 font-medium');

// Span class for required
const spanClass = computed(() => props.bootstrap ? 'text-danger' : 'text-red-500');

// Validation style for non-bootstrap
const validationStyle = computed(() => hasError.value && !props.bootstrap ? "outline: 1px solid red !important;" : "");

// Utility to format title
const toTitleCase = (str) => {
    if (!str) return '';
    return str
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

</script>
